/* VaultEditRenderer.js */

"use strict";
const uiCore = require("@gluenetworks/steve_ui_core");
const ButtonControl = uiCore.Control.Action.Button;
const InputTextControl = uiCore.Control.Textfield.InputText;
const SingleComboBoxControl = uiCore.Control.OptionSelection.Single.ComboBox;
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const BaseRefResolver = Forms.resolvers.ref.base;
const _ = require("lodash");

class VaultEditRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = this._createRootElement();
        this._headerEl = document.createElement("h2");
        this._headerEl.innerText = this._model._labels.title;
        this._previewContainer = document.createElement("div");
        this._previewContainer.classList.add("edit-form");
        this._actionsContainer = document.createElement("div");
        this._actionsContainer.classList.add("actions");
        this._formContainer = document.createElement("div");
        this._formContainer.classList.add("form-container");
        this._refResolver = new BaseRefResolver(model._nibApiService, model._userSession, true);
        this._formsComponent = new FormsComponent(this._refResolver, undefined, false);

        this._msgContainer = document.createElement("div");
        this._msgContainer.classList.add("message");

        this._errorContainer = document.createElement("div");
        this._errorContainer.classList.add("error");

        /* form controls */
        this.saveButton = new ButtonControl({
            label: this._model._labels.saveBtn,
            darkTheme: true,
            disabled: true,
            class: "preview-save",
            active: true,
        });
        this.cancelButton = new ButtonControl({
            label: this._model._labels.cancelBtn,
            darkTheme: true,
            disabled: false,
            class: "preview-close",
        });

        this.testButton = new ButtonControl({
            label: "Test Connection",
            darkTheme: true,
            disabled: true,
            class: "test-connection",
        });

        this._vname = new InputTextControl({
            darkTheme: true,
            disabled: false,
            class: "vault-name",
        });

        this._vendorDropDown = new SingleComboBoxControl({
            showFilter: false,
            darkTheme: true,
            adjustWidth: true,
            allowClear: false,
            options: [
                {
                    value: "cyberArk",
                    label: "CyberArk",
                },
            ],
            disabled: true,
        });

        this._toolDropDown = new SingleComboBoxControl({
            showFilter: false,
            darkTheme: true,
            adjustWidth: true,
            allowClear: false,
            options: [
                {
                    value: "pam",
                    label: "CyberArk",
                },
            ],
            disabled: true,
        });

        this._registerListeners();
        this.render();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        return this._model.whenModelIsReady().then(() => {
            this._connectionConfigJtype = this._getJtype();
            console.log(this._connectionConfigJtype);
            this._vendorDropDown.setValue("cyberArk");
            this._toolDropDown.setValue("pam");
            this._renderProm = this._renderProm || this._render();
            return this._renderProm;
        });
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS    */
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _createSection(content, options = {}) {
        const section = document.createElement("section");
        section.className = options.className || "";
        section.classList.toggle("bordered", !!options.bordered);

        if (options.title) {
            const title = document.createElement("h2");
            title.innerText = options.title;
            section.appendChild(title);
        }
        section.appendChild(content);
        return section;
    }

    _renderFormPair(labelName, tooltip, component, parentEl) {
        const parent = parentEl ? parentEl : this._fieldsSection;
        let labelElem = document.createElement("label");
        labelElem.innerText = labelName;
        labelElem.setAttribute("title", tooltip);
        let cmpntElem = component.renderer.getRootElement();
        parent.appendChild(labelElem);
        parent.appendChild(cmpntElem);
    }

    _registerListeners() {
        this._model.on("Data", (this._onData = this._onData.bind(this)));
        this.saveButton.on("UserClick", this.submitAction.bind(this));
        this.cancelButton.on("UserClick", this.cancelAction.bind(this));

        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = _.debounce(
                this._onFormsPendingPatchesChanged.bind(this),
                500,
                false
            ))
        );
        this._vname.on("UserValue", (this._onNameChange = this._onNameChange.bind(this)));
        this.testButton.on(
            "UserClick",
            (this._onTestConnection = this._onTestConnection.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("Data", this._onData);
        this.saveButton.removeAllListeners("UserClick");
        this.cancelButton.removeAllListeners("UserClick");

        this._formsComponent.removeListener(
            "PendingPatchesChanged",
            this._onFormsPendingPatchesChanged
        );
        this._vname.removeAllListeners();
        this.testButton.removeAllListeners();
    }

    _render() {
        // const root = document.createDocumentFragment();
        this._rootElem.innerHTML = "";
        this._rootElem.appendChild(this._headerEl);
        this._formContainer.appendChild(this._formsComponent.renderer.rootElement);
        const vnameContainer = this._model._uiutils.wrapElement(
            this._vname.renderer.getRootElement(),
            "Name",
            "input-container"
        );
        vnameContainer.title = "Vault name";

        const vendorDropDownContainer = this._model._uiutils.wrapElement(
            this._vendorDropDown.renderer.getRootElement(),
            "Vendor",
            "input-container"
        );
        vendorDropDownContainer.title = "Vendor name";

        const toolDropDownContainer = this._model._uiutils.wrapElement(
            this._toolDropDown.renderer.getRootElement(),
            "Tool Name",
            "input-container"
        );
        toolDropDownContainer.title = "Tool name";
        this._previewContainer.appendChild(vnameContainer);
        this._previewContainer.appendChild(vendorDropDownContainer);
        this._previewContainer.appendChild(toolDropDownContainer);
        this._previewContainer.appendChild(
            this._createSection(this._formContainer, {
                title: "Connection configuration",
                bordered: true,
            })
        );
        this._rootElem.appendChild(this._previewContainer);
        this._rootElem.appendChild(this.testButton.renderer._rootElem);
        this._rootElem.appendChild(this.saveButton.renderer._rootElem);
        this._rootElem.appendChild(this.cancelButton.renderer._rootElem);

        this._rootElem.appendChild(this._msgContainer);
        this._rootElem.appendChild(this._errorContainer);

        this._msgContainer.innerHTML = this._getUpdatedMsg() || "";

        if (this._model._type === "edit") {
            this.testButton.setDisabled(false);
        }

        // last modified by [] on []

        return new Promise((resolve /*,reject*/) => {
            // this._rootElem.appendChild(root);
            this._vname.setValue(this._model._ety.name);
            this._formsComponent.setTypeInformation(this._connectionConfigJtype, true);
            this._formsComponent.setPristineValue(this._getFormPristineValues(), true);
            // this._makeAnchorsNonNavigable();
            return resolve(this._formsComponent.renderer.render());
        });
    }

    _shutdown() {
        this._unregisterListeners();
        this._rootElem.remove();
        this._rootElem = null;

        return Promise.all([
            this.saveButton.shutdown(),
            this.cancelButton.shutdown(),
            this._formsComponent.shutdown(),
            this.testButton.shutdown(),
        ])
            .then(() => {
                this._model = null;
                this._shutdownProm = null;
                this._renderProm = null;
                this._headerEl = undefined;
                this._previewContainer = undefined;
                this._actionsContainer = undefined;
                this._formsComponent = undefined;
                this._msgContainer = undefined;
                this._errorContainer = undefined;
                this.saveButton = undefined;
                this.cancelButton = undefined;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PRIVATE RENDERER METHODS          */
    /*******************************************/

    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("vault-edit", "modal_feedback_container");
        rootElement.component = this._model;
        return rootElement;
    }

    _getFormPristineValues() {
        let formVals = {};
        Object.assign(
            formVals,
            _.get(this._model._ety, "pristineProperties.connectionInformation", {})
        );
        // console.log(formVals);
        console.log(formVals);
        return formVals;
    }

    _getJtype() {
        let jtype = {};
        Object.assign(
            jtype,
            {
                "@Display.layout": {},
            },
            {
                "@Display.styles": {
                    cssClasses: ["dark"],
                },
            }
        );

        Object.assign(jtype, _.get(this._model._integrationsMap, "cyberArk.tools.pam.jtype", {}));

        return jtype;
    }

    _getUpdatedMsg() {
        if (this._model._type === "edit") {
            const updatedBy = this._model._ety.updatedBy;
            const updatedTime = new Date(this._model._ety.updatedOn);
            return `Updated by ${updatedBy} on ${updatedTime.toLocaleString("en-US")}`;
        }
    }

    /*******************************************/
    /* END PRIVATE RENDERER METHODS            */
    /* START HANDLER METHODS                   */
    /*******************************************/
    _onData(data = {}) {
        this._setData(data);
    }

    _isFormInvalid() {
        if (this._formsComponent.isFormInvalid()) {
            return true;
        }
        const nameVal = this._vname.getValue();
        if (!nameVal) {
            return true;
        }
        if (!nameVal.trim()) {
            return true;
        }

        return false;
    }
    _onFormsPendingPatchesChanged() {
        if (this._model) {
            this._errorContainer.innerText = "";
            if (!this._formsComponent.isFormInvalid()) {
                this.testButton.setDisabled(false);
                const vals = this._formsComponent.getValue() || {};
                let connectionInfo = {};
                console.log(vals);
                Object.keys(vals).forEach((key) => {
                    connectionInfo[key] = vals[key];
                });
                if (this._model._type === "edit") {
                    this._model._ety.setConnectionInformation(connectionInfo);
                } else {
                    this._model._ety.pristineProperties.connectionInformation = connectionInfo;
                }
                if (!this._isFormInvalid()) {
                    this._updateButtonState();
                }
            } else {
                this.saveButton.setDisabled(true);
                this.testButton.setDisabled(true);
            }
            // console.log(this._model._ety);
        }
    }

    submitAction() {
        this._model.closePopUp(false);
    }

    cancelAction() {
        this._model.closePopUp(true);
    }

    _onNameChange(val) {
        if (this._model._type === "edit") {
            this._model._ety.setName(val);
        } else {
            this._model._ety.pristineProperties.name = val;
        }

        if (!this._isFormInvalid()) {
            this._updateButtonState();
        } else {
            this.saveButton.setDisabled(true);
        }
    }

    _onTestConnection() {
        this.testButton.setDisabled(true);
        const connectionInfo = this._formsComponent.getValue();
        this._model._VaultsEntity
            .testCredentialSourceConnection(this._model._orgId, "cyberArk", "pam", connectionInfo)
            .then((res) => {
                console.log(res);
                this._errorContainer.innerText = "Connection successful!!!";
            })
            .catch((err) => {
                console.log(err);
                this._errorContainer.innerText = "Connection error!!!";
            })
            .finally(() => {
                this.testButton.setDisabled(false);
            });
    }
    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    _setData() {}

    _updateButtonState() {
        if (this._model._type === "edit") {
            this.saveButton.setDisabled(!this._model._ety.isDirty());
        } else {
            this.saveButton.setDisabled(false);
        }
        if (this._model._readOnly) {
            this.saveButton.setDisabled(true);
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = VaultEditRenderer;
